<template>
  <private-gym-page
    id="reserveringen-page"
    name="reserveringen"
    :has_fab="true"
  >
    <f7-block-title>
      Reserveringen

      <f7-icon
        f7="arrow_2_circlepath"
        class="float-right"
        v-bind:class="{
          rotate: fetchingTrainingSlots,
        }"
        @click="fetchTrainingSlots(true)"
      ></f7-icon>
    </f7-block-title>

    <f7-block v-if="fetchingNews" class="text-align-center">
      <f7-preloader></f7-preloader>
    </f7-block>

    <!--
      NO EMPTY STATE, AND THAT IS THE CONTRACT RATHER THAN AN OMISSION.
      Zero items renders nothing at all — no block, no heading, no placeholder.
      News is supplementary on a page whose purpose is reservations, and a card
      announcing there is no news is noise on the surface a member opened to see
      today's booking. Absence is the state. The admin list in 43-08 is the
      deliberate opposite: there, the list IS the page.

      The list is unbounded: it is never truncated, never paginated, and never
      hidden behind a reveal-the-rest control. Any of those silently withholds
      news an author published and believes is live.
    -->
    <news-card
      v-for="(newsItem, index) in newsItems"
      :key="newsItem.id"
      :title="newsItem.title"
      :subtitle="newsItem.subtitle"
      :height-px="index === 0 ? '300px' : '140px'"
    >
      <template v-slot:content>
        <!--
          `.news-content` is the PRESENTATION scope (43-05). Without it the
          author's HTML renders against Bootstrap 4's `type` styles, where a
          bare h1 is 2.5rem/40px, instead of this app's 15px body scale.

          `sanitiseNewsHtml` is the READ layer (43-03), applied here at the
          point of render rather than assumed from the write path. Write-side
          sanitisation is the primary control, but this app's CSP is stock
          Cordova boilerplate that permits inline script and eval, and
          capacitor.config.json sets allowNavigation ["*"] — so there is no
          other layer behind it. This render also serves legacy rows written
          before the sanitiser existed, and rows the owner still edits directly
          in MySQL.
        -->
        <div class="news-content" v-html="sanitiseNewsHtml(newsItem.content)"></div>
      </template>
    </news-card>

    <!--
      MEDIA-LIST, AND IT IS A CORRECTNESS CHANGE RATHER THAN A COSMETIC ONE
      (41-UI-SPEC IC1). Measured in the installed framework7-bundle.css: in
      non-media mode `.list .item-inner` is `display:flex` and `.list
      .item-title` is `nowrap; overflow:hidden; text-overflow:ellipsis`, so
      there is NOWHERE to put a wrapping status line — anything injected is
      ellipsised or lands inside the row. `.media-list .item-inner` is
      `display:block`, and `item-text` then renders as its own full-width line.

      This UNIFIES the page rather than diverging from it: the empty-state
      branch below has always been a media-list.

      `.reserveringen-list` scopes the two token overrides that come with the
      switch (src/css/components/reserveringen.scss) to this list and nowhere
      else.
    -->
    <f7-list
      media-list
      class="list-strong list-outline list-dividers reserveringen-list"
      v-if="training_slots.length"
    >
      <reservering-list-item
        v-for="training_slot in training_slots"
        :key="training_slot.id"
        :training_slot="training_slot"
        :data-booking-id="training_slot.id"
        @confirm="onRowConfirm(training_slot, $event)"
      ></reservering-list-item>
    </f7-list>
    <f7-list media-list class="list-strong list-outline list-dividers" v-else>
      <f7-list-item
        title="Geen reserveringen gevonden"
        text="Klik op de + onder aan de pagina om een nieuwe training in te plannen"
      >
      </f7-list-item>
    </f7-list>

    <pg-fab @click="openActions = true" v-show="!openActions"></pg-fab>

    <f7-actions :opened="openActions" @actions:close="openActions = false">
      <f7-actions-group>
        <!--        <f7-actions-button @click="openDoor">-->
        <!--          Open de deur-->
        <!--        </f7-actions-button>-->
        <f7-actions-button @click="f7router.navigate('/account/calendar/')">
          Nieuwe reservering
        </f7-actions-button>
      </f7-actions-group>
      <f7-actions-group>
        <f7-actions-button color="red">Cancel</f7-actions-button>
      </f7-actions-group>
    </f7-actions>
  </private-gym-page>
</template>

<script>
import PrivateGymPage from "../../pages/_layouts/PrivateGymPage.vue";
import PgFab from "../../components/general/PgFab.vue";

import moment from "moment/dist/moment";
import "moment/dist/locale/nl";
moment.locale("nl");

import ReserveringListItem from "../../components/account/reserveringen/ReserveringListItem.vue";
import NewsCard from "../../components/general/news/NewsCard.vue";
import { sanitiseNewsHtml } from "../../js/sanitise";

export default {
  components: { NewsCard, ReserveringListItem, PgFab, PrivateGymPage },

  props: {
    f7route: Object,
    f7router: Object,
  },

  mixins: [],

  data: function () {
    return {
      openActions: false,
      training_slots: [],
      fetchingTrainingSlots: false,

      // Renamed from the old newsletter flag rather than newly introduced: the
      // inline preloader block it drives is the one that already shipped on
      // this page, and it is deliberately NOT the global overlay, which covers
      // the navbar and swallows the back gesture.
      //
      // A ~64px placeholder becoming content is the expected layout shift;
      // reserving nothing and popping in a 300px card is a larger one.
      fetchingNews: false,

      from: null,
      to: null,

      daysInPast: 0,

      /**
       * The tapped notification's target, consumed ONCE on mount (41-UI-SPEC IC5).
       *
       * `{ screen, id }` or null. Two uses and no others: scroll-into-view, and
       * deciding whether a confirm records `source: 'push'`. It never steers the
       * path — that is resolveScreenRoute's job, from the allowlist — and it
       * authorises nothing: the API decides whose confirmation it is from the
       * authenticated principal.
       *
       * LOCAL COMPONENT STATE ON PURPOSE. It lives and dies with this page
       * instance, which IS clause 2 of IC5: a member who lands from a push,
       * walks away and comes back later is confirming from inside the app. Put
       * back in the store it would outlive the visit.
       */
      pushTarget: null,
    };
  },

  computed: {
    current_user() {
      return this.$store.state.current_user.data;
    },
    /**
     * The member-facing news list, straight off the store getter.
     *
     * Rendered in the order it arrives. `GET /news` orders `visible_from` DESC
     * then `id` DESC (43-02) and the tie-break is load-bearing there, so
     * re-sorting here would either duplicate that decision or quietly disagree
     * with it.
     */
    newsItems() {
      return this.$store.getters["news/all"];
    },
    dummyTrainingSlot() {
      moment.locale("nl");
      let momentToday = moment();
      momentToday.locale("nl");
      let start = moment().format("YYYY-MM-DD");
      let stop = moment().format("YYYY-MM-DD");
      let start_time = moment().startOf("hour").format("HH:mm");
      let stop_time = moment().startOf("hour").add(1, "hour").format("HH:mm");
      let start_date_time = momentToday
        .startOf("hour")
        .format("dddd D MMMM YYYY, HH:mm");
      start_date_time =
        start_date_time[0].toUpperCase() + start_date_time.substring(1);
      let start_date = moment().format("YYYY-MM-DD");

      return {
        id: "admin",
        start: start,
        stop: stop,
        start_time: start_time,
        stop_time: stop_time,
        gym: {
          id: 1,
          name: "Private Gym",
          street: "Smedenstraat 286",
          city: "Deventer",
          opening_hour: 0,
          closing_hour: 24,
          postcode: "7411RD",
        },
        user: this.current_user,
        user_id: this.current_user.id,
        gym_id: 1,
        start_date_time: start_date_time,
        power_rack: "R.01 - Voorbeeld",
        start_date: start_date,
      };
    },
  },

  created() {
    this.from = moment()
      .subtract(this.daysInPast, "days")
      .startOf("day")
      .format("YYYY-MM-DD HH:mm");
    this.to = moment().endOf("day").format("YYYY-MM-DD HH:mm");
  },

  mounted() {
    this.fetchNews();

    // CONSUMED ONCE, and never read from the store again. `consume_last_tapped_target`
    // returns the parked target the first time and null every time after (41-06), so
    // reading it twice would be reading null the second time and looks like a bug at
    // the call site rather than at the store.
    const target = this.$store.dispatch("push/consume_last_tapped_target");

    Promise.all([target, this.fetchTrainingSlots(false)])
      .then(([tappedTarget]) => {
        this.pushTarget = tappedTarget || null;

        // AFTER the list has rendered, never against an empty list (S5 loading).
        return this.$nextTick().then(() => this.scrollToPushTarget());
      })
      .catch(() => {
        // Deliberately empty, and it is not error swallowing. A failed list load
        // already produced its one message through api/onError; the scroll is a
        // convenience on top of a page that works without it, and a second message
        // about the same failure is the duplicate the one-message rule forbids.
      });
  },

  methods: {
    // The read-side sanitiser, exposed to the template.
    //
    // Through `methods`, deliberately, and NOT by assigning it in `data()`: a
    // function placed in `data` is made reactive state by Vue for no reason —
    // it is a constant module import, not component state.
    sanitiseNewsHtml,

    /**
     * Loads the member's news list.
     *
     * THE `.catch` IS REQUIRED, NOT OPTIONAL, AND IT DELIBERATELY DOES NOTHING.
     * `news/fetch_all` carries `silent: true`, which suppresses the global
     * danger toast for that one request — but 43-06 was explicit that `silent`
     * suppresses the TOAST, never the rejection, so the action still rejects.
     * Without a handler here that rejection is unhandled, and an unhandled
     * rejection is exactly the console noise 43-UI-SPEC.md's one-message rule
     * forbids alongside the toast and the error block.
     *
     * So: no toast, no error block, no console noise, and no member-facing
     * error surface at all. A supplementary panel failing must not put a red
     * bar over a working reservations page — and if the API is genuinely down,
     * `fetchTrainingSlots` on this same page has already said so once.
     *
     * The flag is cleared in `.finally` so it clears on failure as well as on
     * success. Clearing it only in the success path would leave a preloader
     * spinning forever on a page that otherwise works.
     */
    fetchNews() {
      this.fetchingNews = true;

      this.$store
        .dispatch("news/fetch_all", {})
        .catch(() => {
          // Intentionally empty. See above — this is the one-message rule, not
          // swallowed error handling: nothing downstream needs to know, and
          // there is no member-facing surface to report it on.
        })
        .finally(() => {
          this.fetchingNews = false;
        });
    },
    /**
     * Loads the member's bookings.
     *
     * The chain is RETURNED now (41-07). `mounted()` has to know the list has
     * rendered before it can scroll to the row a push named — scrolling to a row
     * that does not exist yet is a silent no-op, which is exactly the kind of
     * check that passes whether or not the work happened.
     *
     * `created()`'s `from`/`to` and `daysInPast: 0` are UNTOUCHED. The server
     * ignores `to` and returns all upcoming bookings, which is WHY a T-72h
     * tap-through lands on a list that contains the booking (41-UI-SPEC H2, an
     * owner-ruled invariant). Narrowing this request would break the landing.
     */
    fetchTrainingSlots(showNotification) {
      this.fetchingTrainingSlots = true;
      // setTimeout(() => {
      //   this.fetchingTrainingSlots = false;
      // },1000)
      return this.$store
        .dispatch("current_user/fetch_training_slots", {
          from: this.from,
          to: this.to,
        })
        .then((training_slots) => {
          let prependedArray = training_slots.slice();
          if (this.current_user.is_admin) {
            prependedArray.unshift(this.dummyTrainingSlot);
          }
          this.training_slots = prependedArray;
          if (showNotification) {
            this.$store.dispatch("notifications/addSuccessNotification", {
              message: "Reserveringen ingeladen",
            });
          }
        })
        .finally(() => {
          setTimeout(() => {
            // Zo lijkt het langer te laden
            this.fetchingTrainingSlots = false;
          }, 1000);
        });
    },

    /**
     * Scroll the booking a notification tap named into view (41-UI-SPEC S5/IC5).
     *
     * NO HIGHLIGHT is added, deliberately: the row carrying the only fill button
     * on the screen is already the strongest marker there is, and every row states
     * its own date, so confirming the wrong booking is not possible by misreading
     * a highlight.
     *
     * Every step is feature-detected rather than assumed. The test environment is
     * happy-dom, which performs no layout and does not implement scrollIntoView;
     * an unguarded call would throw inside a mounted hook and take the page's
     * whole render with it, for a convenience.
     */
    scrollToPushTarget() {
      const target = this.pushTarget;

      if (!target || target.id === null || typeof target.id === "undefined") {
        return;
      }

      const root = this.$el;

      if (!root || typeof root.querySelector !== "function") {
        return;
      }

      // Matched on the attribute the row already carries, so the lookup names the
      // booking rather than a position in the list. A position would scroll to the
      // wrong booking the moment the admin dummy row is unshifted in front.
      const row = root.querySelector(
        '[data-booking-id="' + String(target.id).replace(/["\\]/g, "") + '"]'
      );

      if (row && typeof row.scrollIntoView === "function") {
        row.scrollIntoView();
      }
    },

    /**
     * One tap on a row's `Bevestig` button (BOOK-02, 41-UI-SPEC IC4/IC5).
     *
     * THE `source` DECISION LIVES HERE, not on the button, because the binding
     * belongs to the NAVIGATION that opened this page and the button knows nothing
     * about it. `source: 'push'` iff this is the booking the consumed payload
     * named and the binding has not been spent.
     *
     * The binding is spent by the first confirm OF THAT ID — success or failure,
     * because a retry after a failure is a second confirm. Confirming a DIFFERENT
     * booking leaves it alone: that booking is still the one the push was about.
     *
     * NOT OPTIMISTIC (IC4). Nothing is written here before the server answers, and
     * on success the server's echo REPLACES the booking's `confirmations` object
     * wholesale — no merge, no per-field patch. A partial merge is how two sources
     * of the same truth start to disagree, and here the disagreement would be a
     * row reading `Bevestigd` over a stage the server still considers open.
     *
     * @param {Object} training_slot the row's own booking
     * @param {{done?: Function}} [payload] the row's in-flight callback, called in
     *   BOTH outcomes — the row owns its button's state and this page owns the
     *   request, so only this page knows when the server answered. Never calling
     *   it leaves that button disabled for the rest of the visit.
     * @returns {Promise} resolves when the row has been updated or the failure handled
     */
    onRowConfirm(training_slot, payload) {
      const isPushTarget =
        !!this.pushTarget &&
        String(this.pushTarget.id) === String(training_slot.id);

      return this.$store
        .dispatch("training_slots/confirm", {
          training_slot_id: training_slot.id,
          source: isPushTarget ? "push" : "app",
        })
        .then((confirmed) => {
          if (!confirmed || !confirmed.confirmations) {
            return;
          }

          this.training_slots = this.training_slots.map((slot) =>
            String(slot.id) === String(training_slot.id)
              ? { ...slot, confirmations: confirmed.confirmations }
              : slot
          );
        })
        .catch(() => {
          // The store action already emitted the ONE message this failure gets and
          // re-threw so the row could re-enable its button. Nothing further is owed
          // here, and the row is deliberately left exactly as it was.
        })
        .finally(() => {
          if (payload && typeof payload.done === "function") {
            payload.done();
          }

          if (isPushTarget) {
            this.pushTarget = null;
          }
        });
    },
  },

  watch: {
    //
  },
};
</script>
